import { getCurrentAccount } from './account_helpers';
import { eraseCookie, getCookie, setCookie } from './api/helpers';
import { routes } from './routes';

export const toBase64 = (file: File) =>
  new Promise<string | undefined>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () =>
      resolve(reader.result?.toString().replace(/^data:(.*,)?/, ''));
    reader.onerror = (error) => reject(error);
  });

// ensures redirect is from same URL
export const isSameOriginRedirect = (urlString: string, base: string) => {
  const url = new URL(base);
  const redirect = new URL(urlString, base);

  return url.origin !== redirect.origin;
};

export const addAccountID = (params: any) => {
  const accountID = getCurrentAccount();
  if (!accountID) {
    window.location.href = routes.login;
  }
  return {
    ...params,
    accountID,
  };
};

export function setPartnerStatus(isNew: boolean) {
  if (isNew) {
    setCookie('newPartner', '1', 1);
  } else {
    eraseCookie('newPartner');
  }
}

export function isNewPartner() {
  return getCookie('newPartner') === '1';
}

export type CountryOption = {
  label: string;
  value: string; // iso3166 alpha-2 country codes
  taxIDType:
    | 'ae_trn'
    | 'au_abn'
    | 'br_cnpj'
    | 'br_cpf'
    | ' ca_bn'
    | 'ca_gst_hst'
    | 'ca_pst_bc'
    | 'ca_pst_mb'
    | 'ca_pst_sk'
    | 'ca_qst'
    | 'ch_vat'
    | 'cl_tin'
    | 'es_cif'
    | 'eu_vat'
    | 'gb_vat'
    | ' hk_br'
    | 'id_npwp'
    | 'il_vat'
    | 'in_gst'
    | ' jp_cn'
    | ' jp_rn'
    | 'kr_brn'
    | 'li_uid'
    | 'mx_rfc'
    | 'my_frp'
    | 'my_itn'
    | 'my_sst'
    | 'no_vat'
    | 'nz_gst'
    | 'ru_inn'
    | 'ru_kpp'
    | 'sa_vat'
    | 'sg_gst'
    | 'sg_uen'
    | 'th_vat'
    | 'tw_vat'
    | 'us_ein'
    | 'za_vat'; // Must be a valid StripeTaxID type
};

export const COUNTRY_OPTIONS: CountryOption[] = [
  {
    label: 'United Kingdom',
    value: 'GB',
    taxIDType: 'gb_vat',
  },
  {
    label: 'United States',
    value: 'US',
    taxIDType: 'us_ein',
  },
  {
    label: 'Austria',
    value: 'AT',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Belgium',
    value: 'BE',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Bulgaria',
    value: 'BG',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Croatia',
    value: 'HR',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Cyprus',
    value: 'CY',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Czech Republic',
    value: 'CZ',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Denmark',
    value: 'DK',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Estonia',
    value: 'EE',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Finland',
    value: 'FI',
    taxIDType: 'eu_vat',
  },
  {
    label: 'France',
    value: 'FR',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Germany',
    value: 'DE',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Greece',
    value: 'GR',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Hungary',
    value: 'HU',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Ireland',
    value: 'IE',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Italy',
    value: 'IT',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Latvia',
    value: 'LV',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Lithuania',
    value: 'LT',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Luxembourg',
    value: 'LU',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Malta',
    value: 'MT',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Netherlands',
    value: 'NL',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Poland',
    value: 'PL',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Portugal',
    value: 'PT',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Romania',
    value: 'RO',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Slovakia',
    value: 'SK',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Slovenia',
    value: 'SI',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Spain',
    value: 'ES',
    taxIDType: 'eu_vat',
  },
  {
    label: 'Sweden',
    value: 'SE',
    taxIDType: 'eu_vat',
  },
];

export const formatCurrency = (currency: string, amount: number) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 20,
  }).format(amount);
};
